import React, {
  useState,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
} from "react";
import cx from "classnames";
// import PropTypes from 'prop-types'
import { DateTimeStyled } from "./styled";
import DatePicker from "react-datepicker";
import {
  MdChevronLeft,
  MdChevronRight,
  MdArrowDropDown,
} from "react-icons/md";
import "react-datepicker/dist/react-datepicker.css";
// import moment from 'moment';

const MONTHS = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const range = (start, end) => {
  const result = [];
  for (let i = start; i <= end; i += 1) result.push(i);
  return result;
};

const CalendarSelect = ({ value, options, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const handleEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={`dp_cs ${className || ""}`} ref={ref}>
      <button
        type="button"
        className="dp_cs_btn"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dp_cs_label">{selected?.label}</span>
        <MdArrowDropDown size={18} />
      </button>
      {open && (
        <ul className="dp_cs_menu" role="listbox">
          {options.map((o) => {
            const isActive = o.value === value;
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isActive}
                className={`dp_cs_item ${isActive ? "active" : ""}`}
                ref={
                  isActive
                    ? (el) => el && el.scrollIntoView({ block: "nearest" })
                    : null
                }
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const DateTime = ({
  theme_standard,
  theme_filter,
  errors,
  value,
  onChange,
  label,
  dateFormat = "dd/MM/yyyy",
  showMonthYearPicker,
  showYearPicker,
  disabled,
  placeholder,
  yearRange,
  ...props
}) => {
  const customClass = cx({
    theme_standard: theme_standard,
    theme_filter: theme_filter,
  });

  const [startDate, setStartDate] = useState();

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const from = yearRange?.from ?? currentYear - 50;
    const to = yearRange?.to ?? currentYear + 5;
    return range(from, to);
  }, [yearRange]);

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <button
      type="button"
      className={`date_container ${disabled ? "disabled" : ""}`}
      onClick={onClick}
      ref={ref}
    >
      {value || placeholder}
    </button>
  ));

  useEffect(() => {
    // ต้อง sync ตอนถูกล้างค่าด้วย (value -> null)
    // เดิมเช็คแค่ if (value) ทำให้วันที่เดิมค้างบนหน้าจอ ทั้งที่ค่าในฟอร์มถูกล้างไปแล้ว
    setStartDate(value ? new Date(value) : null);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const _handleChange = (e) => {
    setStartDate(e);
    onChange(e);
  };

  const useCustomHeader = !showMonthYearPicker && !showYearPicker;

  const monthOptions = useMemo(
    () => MONTHS.map((m, i) => ({ label: m, value: i })),
    []
  );
  const yearOptions = useMemo(
    () => years.map((y) => ({ label: String(y), value: y })),
    [years]
  );

  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="dp_header">
      <button
        type="button"
        className="dp_nav_btn"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        aria-label="Previous month"
      >
        <MdChevronLeft size={20} />
      </button>
      <div className="dp_selects">
        <CalendarSelect
          className="dp_cs_month"
          value={date.getMonth()}
          options={monthOptions}
          onChange={(v) => changeMonth(v)}
        />
        <CalendarSelect
          className="dp_cs_year"
          value={date.getFullYear()}
          options={yearOptions}
          onChange={(v) => changeYear(v)}
        />
      </div>
      <button
        type="button"
        className="dp_nav_btn"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        aria-label="Next month"
      >
        <MdChevronRight size={20} />
      </button>
    </div>
  );

  return (
    <DateTimeStyled>
      <div className={customClass}>
        {label && <div className="label">{label}</div>}
        <DatePicker
          {...props}
          disabled={disabled}
          dateFormat={dateFormat}
          showMonthYearPicker={showMonthYearPicker}
          showYearPicker={showYearPicker}
          selected={startDate}
          onChange={(date) => _handleChange(date)}
          customInput={<ExampleCustomInput />}
          calendarClassName="dp_calendar"
          {...(useCustomHeader && { renderCustomHeader })}
        />
        {errors && <div className="errors">{errors}</div>}
      </div>
    </DateTimeStyled>
  );
};

DateTime.propTypes = {};

export default DateTime;
